import { useState } from 'react';
import { ClientsService } from 'services/clients.service';
import { User } from 'interfaces/user.interface';
import { ToastService } from 'services/toast.service';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, CommonSectorService, CommonSiteService, Site, UserFilter } from '@umanforge/common';
import { UsersService } from 'services/users.service';
import { EventManagerService } from 'services/eventManagers.service';
import { ThemeService } from 'services/themes.service';
import { SectorWhiteTable } from 'interfaces/sector.interface';
import { EventManagerWithPosition } from 'interfaces/eventManager.interface';
import { TableCreation } from 'interfaces/types.interface';

const toastService: ToastService = new ToastService( 'Home API' );

function useHomeAPI() {
  const [ sites, setSites ] = useState<Site[]>( [] );
  const [ sectorsBySite ] = useState<{ [key: string]: SectorWhiteTable[] }>( {} );
  const [ tablesBySector, setTablesBySector ] = useState<{ [key: string]: EventManagerWithPosition[] }>( {} );
  const [ isLoading, setIsLoading ] = useState<boolean>( false );
  const [ error, setError ] = useState<string | null>( null );

  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );

  //-------------- Fetch sites --------------\\
  const getSites = async ( activeClientId: string, forceRefresh = false ) => {
    if ( !forceRefresh && sites.length > 0 ) {
      return sites;
    }

    setIsLoading( true );
    try {
      const fetchedSites = await CommonSiteService.getAllowedSitesByClientsId( [ activeClientId ], token, dispatch );
      setSites( fetchedSites );
      return fetchedSites;
    } catch ( error2 ) {
      console.error( 'Error fetching sites:', error2 );
      setError( 'Error fetching sites' );
      return [];
    } finally {
      setIsLoading( false );
    }
  };

  //-------------- Fetch sectors for a given site --------------\\
  const getSectorsBySiteId = async ( siteId: string, forceRefresh = false ) => {
    if ( !forceRefresh && sectorsBySite[ siteId ] ) {
      return sectorsBySite[ siteId ];
    }

    setIsLoading( true );
    try {
      const fetchedSectors = await CommonSectorService.getAllowedSectorsBySitesId( [ siteId ], token, dispatch );
      return fetchedSectors;
    } catch ( error3 ) {
      console.error( 'Error fetching sectors:', error3 );
      setError( 'Error fetching sectors' );
      return [];
    } finally {
      setIsLoading( false );
    }
  };

  //-------------- Fetch tables for a given sector --------------\\
  const getTablesBySectorId = async ( sectorId: string, forceRefresh = false ) => {
    if ( !forceRefresh && tablesBySector[ sectorId ] ) {
      return tablesBySector[ sectorId ];
    }

    setIsLoading( true );
    try {
      const fetchedTables = await EventManagerService.getTablesBySector( sectorId, token, dispatch );
      setTablesBySector( prev => {return { ...prev, [ sectorId ]: fetchedTables };} );
      return fetchedTables;
    } catch ( error4 ) {
      console.error( 'Error fetching tables:', error4 );
      setError( 'Error fetching tables' );
      return [];
    } finally {
      setIsLoading( false );
    }
  };

  //-------------- Create tables --------------\\
  const createTableInSectors = async ( newTable: TableCreation ) => {
    setIsLoading( true );
    try {
      await EventManagerService.createTable( newTable, token, dispatch );

      toastService.showSuccess( 'Table créer' );
    } catch {
      toastService.showError( 'Erreur dans la création du tableau' );
      setError( 'Error creating table in sectors' );
    } finally {
      setIsLoading( false );
    }
  };

  //-------------- Fetch participants for a table --------------\\
  const getTableParticipants = async ( eventManagerId: string[] ) => {
    try {
      setIsLoading( true );
      const participants = await EventManagerService.getTableParticipants( eventManagerId, token, dispatch );
      return participants;
    } catch ( error6 ) {
      console.error( 'Error fetching participants:', error6 );
      setError( 'Error fetching participants' );
      return [];
    } finally {
      setIsLoading( false );
    }
  };


  //-------------- Post Update the position of the tables in a sector --------------\\
  const updateTablePositionsAPI = async ( sectorId: string, eventManagers: { eventManagerId: string, position: number }[] ) => {
    setIsLoading( true );
    try {
      await EventManagerService.updateTablePositions( sectorId, eventManagers, token, dispatch );
    } catch ( error6 ) {
      console.error( 'Error updating table positions:', error6 );
      setError( 'Error updating table positions' );
    } finally {
      setIsLoading( false );
    }
  };

  //-------------- Fetch users --------------\\
  const getUsers = async (): Promise<User[]> => {
    setIsLoading( true );
    try {
      // Appel à fetchUsers, qui renvoie { total, users }
      const userFilter: UserFilter = {
        page: 1,
        step: 10,
        filterName: undefined,
        filterClients: undefined,
        filterSites: undefined,
        filterSectors: undefined
      };

      const response = await UsersService.getAllowedUsers( userFilter );

      // Accède au tableau des utilisateurs à partir de la réponse
      const users: User[] = response.users;

      return users;
    } catch ( error7 ) {
      console.error( 'Error fetching users:', error7 );
      setError( 'Error fetching users' );
      return [];
    } finally {
      setIsLoading( false );
    }
  };


  //-------------- Fetch Themes --------------\\
  const getThemes = async ( eventManagerId: string ) => {
    try {
      const fetchedThemes = await ThemeService.getThemesByEventManager( [ eventManagerId ], token, dispatch );
      return fetchedThemes;
    } catch ( error8 ) {
      console.error( 'Failed to fetch themes:', error8 );
      throw new Error( 'Failed to fetch themes' );
    }
  };

  //-------------- Fetch clients --------------\\
  const getClients = async () => {

    setIsLoading( true );
    try {
      const clients = await ClientsService.getClients( token, dispatch );
      return clients;
    } catch ( error9 ) {
      console.error( 'Error fetching clients:', error9 );
      setError( 'Error fetching clients' );
      return [];
    } finally {
      setIsLoading( false );
    }
  };

  return {
    getSites,
    getSectorsBySiteId,
    getTablesBySectorId,
    createTableInSectors,
    getTableParticipants,
    updateTablePositionsAPI,
    getClients,
    getUsers,
    getThemes,
    isLoading,
    error,
  };
}

export default useHomeAPI;
